import { useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { MapPin } from "lucide-react";
import { recordGpsError } from "@/lib/errorTrail";

type Props = {
  deliveryRequestId: string;
  isActive: boolean;
  onPosition?: (pos: { lat: number; lng: number }) => void;
};

// Coordinates the two GPS consumers (delivery tracking here, mileage in
// useTripTracker): running the @capgo background engine and another engine
// concurrently has ANR'd the app before, so whichever starts the background
// engine sets this flag and the other must stay on the foreground poller.
const DELIVERY_BG_FLAG = "deliveryBgActive";
const BG_FIX_WATCHDOG_MS = 30_000;
const POST_THROTTLE_MS = 5_000;
// A coarse cell/wifi fix can read kilometres off. We prefer fixes at least this
// accurate (matches tripStore) but never stop sharing location entirely — see
// postLocation. GOOD_FIX_TTL_MS matches the server's driver_locations staleness
// window: as long as an accurate fix is that fresh, a coarse one can't clobber it.
const ACCURACY_MAX_M = 100;
const GOOD_FIX_TTL_MS = 5 * 60 * 1000;

// Delivery-run mileage (same jitter/teleport filters as the mileage tracker).
// Persisted per delivery so a WebView reload mid-run doesn't lose the miles;
// Deliveries.tsx reads and clears it when the delivery is confirmed.
const MIN_SEG_MI = 0.003;
const MAX_SEG_MI = 5;
export const deliveryMilesKey = (deliveryRequestId: string) => `deliveryMiles:${deliveryRequestId}`;

function haversineMiles(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const R = 3958.7613;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLng = ((bLng - aLng) * Math.PI) / 180;
  const lat1 = (aLat * Math.PI) / 180;
  const lat2 = (bLat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export function DriverGPSSender({ deliveryRequestId, isActive, onPosition }: Props) {
  const [sending, setSending] = useState(false);
  const [lastSent, setLastSent] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [bgLive, setBgLive] = useState(false);
  const intervalRef = useRef<number | null>(null);
  const bgActiveRef = useRef(false);
  const watchdogRef = useRef<number | null>(null);
  const hadBgFixRef = useRef(false);
  const lastPostAtRef = useRef(0);
  const lastGoodFixAtRef = useRef(0);
  const postingRef = useRef(false);
  const lastFixRef = useRef<{ lat: number; lng: number } | null>(null);
  const milesRef = useRef(0);
  const [miles, setMiles] = useState(0);

  useEffect(() => {
    if (!isActive) return;

    let cancelled = false;

    // Resume any miles already logged for this delivery (reload mid-run).
    try {
      const saved = Number(window.localStorage.getItem(deliveryMilesKey(deliveryRequestId)));
      if (Number.isFinite(saved) && saved > 0) {
        milesRef.current = saved;
        setMiles(saved);
      }
    } catch { /* ignore */ }

    const postLocation = async (
      lat: number,
      lng: number,
      heading: number | null,
      speed: number | null,
      accuracy: number | null,
    ) => {
      const now = Date.now();
      const accurate = !(typeof accuracy === "number" && accuracy > 0 && accuracy > ACCURACY_MAX_M);
      // A coarse fix can land kilometres off and stranded a driver standing at
      // the stop. Don't let one overwrite a recent accurate position — but if we
      // have no fresh accurate fix (weak-signal area), keep sharing the best we
      // have so tracking and the confirm gate aren't left blind; the gate's
      // distance override backstops a wrong reading rather than a hard block.
      if (!accurate && now - lastGoodFixAtRef.current < GOOD_FIX_TTL_MS) return;
      onPosition?.({ lat, lng });
      const prev = lastFixRef.current;
      lastFixRef.current = { lat, lng };
      if (prev) {
        const seg = haversineMiles(prev.lat, prev.lng, lat, lng);
        if (seg >= MIN_SEG_MI && seg <= MAX_SEG_MI) {
          milesRef.current += seg;
          setMiles(milesRef.current);
          try {
            window.localStorage.setItem(deliveryMilesKey(deliveryRequestId), String(milesRef.current));
          } catch { /* ignore */ }
        }
      }
      if (postingRef.current || now - lastPostAtRef.current < POST_THROTTLE_MS) return;
      postingRef.current = true;
      lastPostAtRef.current = now;
      try {
        setSending(true);
        await fetch("/api/delivery-tracking/update-location", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ deliveryRequestId, latitude: lat, longitude: lng, heading, speed }),
        });
        if (accurate) lastGoodFixAtRef.current = now;
        setLastSent(new Date());
        setError(null);
      } catch (err: any) {
        setError(err?.message || "Network error");
      } finally {
        setSending(false);
        postingRef.current = false;
      }
    };

    const readForegroundFix = async () => {
      try {
        let lat: number, lng: number, heading: number | null = null, speed: number | null = null, accuracy: number | null = null;

        if (Capacitor.isNativePlatform()) {
          const { Geolocation } = await import("@capacitor/geolocation");
          // maximumAge: 0 is critical — a cached fix would freeze the marker
          // while the driver is actually moving.
          const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 30000, maximumAge: 0 });
          lat = pos.coords.latitude;
          lng = pos.coords.longitude;
          heading = pos.coords.heading;
          speed = pos.coords.speed != null ? Math.round(pos.coords.speed * 2.237) : null; // m/s to mph
          accuracy = pos.coords.accuracy ?? null;
        } else if ("geolocation" in navigator) {
          const pos = await new Promise<GeolocationPosition>((resolve, reject) =>
            navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 30000, maximumAge: 0 })
          );
          lat = pos.coords.latitude;
          lng = pos.coords.longitude;
          heading = pos.coords.heading;
          speed = pos.coords.speed != null ? Math.round(pos.coords.speed * 2.237) : null;
          accuracy = pos.coords.accuracy ?? null;
        } else {
          setError("Location not available");
          return;
        }

        await postLocation(lat, lng, heading, speed, accuracy);
      } catch (err: any) {
        setError(err?.message || "GPS error");
      }
    };

    const startForeground = () => {
      if (intervalRef.current || cancelled) return;
      void readForegroundFix();
      intervalRef.current = window.setInterval(() => void readForegroundFix(), 6_000);
    };

    const stopBackground = async () => {
      if (!bgActiveRef.current) return;
      bgActiveRef.current = false;
      setBgLive(false);
      try {
        window.localStorage.removeItem(DELIVERY_BG_FLAG);
      } catch { /* ignore */ }
      try {
        const { BackgroundGeolocation } = await import("@capgo/background-geolocation");
        await BackgroundGeolocation.stop();
      } catch { /* ignore */ }
    };

    const startBackground = async (): Promise<boolean> => {
      // Single-engine rule: never start the background engine while the mileage
      // tracker is running its own GPS engine (two engines at once has ANR'd
      // the app before).
      try {
        const trip = window.localStorage.getItem("mileageTrip");
        if (trip && JSON.parse(trip)?.phase === "tracking") return false;
      } catch { /* unreadable state — assume no trip */ }
      if (!Capacitor.isNativePlatform() || !Capacitor.isPluginAvailable("BackgroundGeolocation")) {
        return false;
      }
      try {
        const { BackgroundGeolocation } = await import("@capgo/background-geolocation");
        await BackgroundGeolocation.start(
          {
            backgroundTitle: "Delivering for Stoop",
            backgroundMessage: "Sharing your live location for this delivery. Tap to return.",
            requestPermissions: false,
            stale: false,
            distanceFilter: 10,
          },
          (position, err) => {
            if (cancelled || !bgActiveRef.current) return;
            if (err) {
              recordGpsError(`delivery-bg: ${err.message || err.code || "error"}`);
              return;
            }
            if (!position || !Number.isFinite(position.latitude)) return;
            hadBgFixRef.current = true;
            void postLocation(
              position.latitude,
              position.longitude,
              null,
              typeof position.speed === "number" ? Math.round(position.speed * 2.237) : null,
              typeof position.accuracy === "number" ? position.accuracy : null,
            );
          },
        );
        bgActiveRef.current = true;
        setBgLive(true);
        try {
          window.localStorage.setItem(DELIVERY_BG_FLAG, "1");
        } catch { /* ignore */ }
        // Watchdog: if the background engine delivers nothing, kill it and fall
        // back to the proven foreground poller rather than tracking blind.
        hadBgFixRef.current = false;
        watchdogRef.current = window.setTimeout(() => {
          if (cancelled) return;
          if (!hadBgFixRef.current) {
            recordGpsError("delivery-bg: no fix in 30s — falling back to foreground");
            void stopBackground().then(() => {
              if (!cancelled) startForeground();
            });
          }
        }, BG_FIX_WATCHDOG_MS);
        return true;
      } catch (e: any) {
        recordGpsError(`delivery-bg start failed: ${e?.message || e}`);
        return false;
      }
    };

    void startBackground().then((ok) => {
      if (!ok && !cancelled) startForeground();
    });

    return () => {
      cancelled = true;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (watchdogRef.current) {
        clearTimeout(watchdogRef.current);
        watchdogRef.current = null;
      }
      void stopBackground();
    };
  }, [isActive, deliveryRequestId]);

  if (!isActive) return null;

  return (
    <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-[11px] text-green-700 dark:border-green-800/40 dark:bg-green-950/30 dark:text-green-400">
      <MapPin className="h-3.5 w-3.5 shrink-0" />
      <span>
        {sending ? "Sending location..." : error ? `GPS: ${error}` : lastSent ? `Location shared ${Math.round((Date.now() - lastSent.getTime()) / 1000)}s ago${bgLive ? " · stays live in background" : ""}${miles > 0.05 ? ` · ${miles.toFixed(1)} mi` : ""}` : "Starting GPS..."}
      </span>
      <span className="ml-auto h-2 w-2 rounded-full bg-green-500 animate-pulse" />
    </div>
  );
}
